/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: pages\dashboards\widgets\SystemStatsWidget.tsx
 ??? Created: 2026-05-02
 ?? Description:
    Implementation of the PBX Tools module.
----------------------------------------------------------------*/


import { useState, useEffect, useContext } from 'react';
import { Cpu, MemoryStick, HardDrive, Activity } from 'lucide-react';
import { WidgetContext } from '../components/WidgetWrapper';

interface SystemStats {
    cpu: { usage: number; load?: number[] };
    memory: { used: number; total: number; percentage?: number; usage?: number };
    disk: { used: number; total: number; percentage: number }[] | { used: number; total: number; percentage: number };
}

interface SystemStatsWidgetProps {
    config?: Record<string, any>;
    onConfigChange?: (config: Record<string, any>) => void;
    isEditMode?: boolean;
}

// Default widget preferences for card styling
const DEFAULT_CARD_COLOR = '#1e293b';
const DEFAULT_BORDER_COLOR = '#334155';

const CircularGauge = ({
    value,
    label,
    icon: Icon,
}: {
    value: number;
    label: string;
    icon: React.ElementType;
}) => {
    // Dynamic scaling using viewBox
    const radius = 40;
    const circumference = 2 * Math.PI * radius;
    const progress = (value / 100) * circumference;

    const getColor = () => {
        if (value >= 90) return 'text-red-500';
        if (value >= 70) return 'text-yellow-500';
        return 'text-green-500';
    };

    return (
        <div className="flex flex-col items-center justify-center w-full h-full min-h-0">
            <div className="relative h-full aspect-square max-h-[120px] p-2">
                <svg className="w-full h-full transform -rotate-90" viewBox="0 0 100 100">
                    <circle
                        cx="50"
                        cy="50"
                        r={radius}
                        stroke="currentColor"
                        strokeWidth="8"
                        fill="none"
                        className="text-muted/30"
                    />
                    <circle
                        cx="50"
                        cy="50"
                        r={radius}
                        stroke="currentColor"
                        strokeWidth="8"
                        fill="none"
                        strokeLinecap="round"
                        strokeDasharray={circumference}
                        strokeDashoffset={circumference - progress}
                        className={getColor()}
                    />
                </svg>
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                    <Icon className={`w-1/4 h-1/4 mb-1 ${getColor()}`} />
                    <span className={`text-sm font-bold ${getColor()}`}>{Math.round(value)}%</span>
                </div>
            </div>
            <span className="text-xs text-muted-foreground font-medium truncate max-w-full">{label}</span>
        </div>
    );

};

// Card wrapper for each gauge
const GaugeCard = ({ children }: { children: React.ReactNode }) => {
    const widgetPrefs = useContext(WidgetContext);
    const cardColor = widgetPrefs?.cardColor || DEFAULT_CARD_COLOR;
    const borderColor = widgetPrefs?.borderColor || DEFAULT_BORDER_COLOR;

    return (
        <div
            className="flex items-center justify-center p-1 rounded-lg w-full h-full overflow-hidden"
            style={{
                backgroundColor: cardColor,
                border: `1px solid ${borderColor}`
            }}
        >
            {children}
        </div>
    );
};

export function SystemStatsWidget({ config, isEditMode }: SystemStatsWidgetProps) {
    const [stats, setStats] = useState<SystemStats | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const fetchStats = async () => {
            try {
                const protocol = window.location.protocol;
                const host = window.location.host;
                const response = await fetch(`${protocol}//${host}/monitoring-api/api/system/stats`);

                if (response.ok) {
                    const data = await response.json();
                    if (data.success && data.data) {
                        setStats(data.data);
                        setError(null);
                    }
                }
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            } catch (err: any) {
                setError('Failed to load stats');
            } finally {
                setLoading(false);
            }
        };

        fetchStats();

        // Refresh every 5 seconds
        const interval = setInterval(fetchStats, 5000);
        return () => clearInterval(interval);
    }, []);

    if (loading) {
        return (
            <div className="h-full flex items-center justify-center">
                <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
        );
    }

    if (error || !stats) {
        return (
            <div className="h-full flex items-center justify-center text-muted-foreground">
                <span className="text-sm">{error || 'No data available'}</span>
            </div>
        );
    }

    // Get memory percentage (API returns 'usage' but interface might use 'percentage')
    const memoryPercentage = stats.memory?.percentage ?? stats.memory?.usage ?? 0;

    // Get disk percentage (could be array or single object)
    let diskPercentage = 0;
    if (Array.isArray(stats.disk)) {
        // If array, get the root partition (usually the most relevant)
        const rootDisk = stats.disk.find(d => (d as any).mountpoint === '/') || stats.disk[0];
        diskPercentage = rootDisk?.percentage ?? (rootDisk as any)?.usePercent ?? 0;
    } else {
        diskPercentage = stats.disk?.percentage ?? 0;
    }

    // Get load average
    const loadAverage = stats.cpu?.load || [0, 0, 0];

    return (
        <div className="h-full flex flex-col">
            {/* Gauges in cards */}
            <div className="flex-1 flex items-stretch justify-center gap-2 px-2 overflow-hidden mb-2">
                <div className="flex-1 min-w-0">
                    <GaugeCard>
                        <CircularGauge
                            value={stats.cpu?.usage || 0}
                            label="CPU"
                            icon={Cpu}
                        />
                    </GaugeCard>
                </div>
                <div className="flex-1 min-w-0">
                    <GaugeCard>
                        <CircularGauge
                            value={memoryPercentage}
                            label="Memory"
                            icon={MemoryStick}
                        />
                    </GaugeCard>
                </div>
                <div className="flex-1 min-w-0">
                    <GaugeCard>
                        <CircularGauge
                            value={diskPercentage}
                            label="Disk"
                            icon={HardDrive}
                        />
                    </GaugeCard>
                </div>
            </div>

            {/* Load Average */}
            <div className="flex items-center justify-center gap-3 py-1 px-3 border-t border-border/50">
                <Activity className="w-3 h-3 text-muted-foreground" />
                <span className="text-xs text-muted-foreground">Load:</span>
                <div className="flex items-center gap-2 text-xs font-mono">
                    <span className="text-foreground">{loadAverage[0]?.toFixed(2) || '0.00'}<span className="text-muted-foreground ml-0.5">1m</span></span>
                    <span className="text-foreground">{loadAverage[1]?.toFixed(2) || '0.00'}<span className="text-muted-foreground ml-0.5">5m</span></span>
                    <span className="text-foreground">{loadAverage[2]?.toFixed(2) || '0.00'}<span className="text-muted-foreground ml-0.5">15m</span></span>
                </div>
            </div>
        </div>
    );
}
